// Field render pass: the static aerodrome scenery every level draws first —
// water, grass, taxiway, runway, pad — before any ink, ripple or aircraft
// goes down on top of it. Ported 1:1 out of `canvas.ts`'s `draw()`; no
// behaviour changed, only `this.ctx` → an explicit `frame.ctx`.
import type { World, Runway, Pad, Vec } from '../sim/index.ts';
import { COL, pulse } from './theme.ts';
import { roundRect } from './decor.ts';
import type { FrameCtx } from './canvas.ts';

/** `level.water` (a fixed list of screen fractions) → logical-px points,
 *  computed once per (W, H) and reused every frame — see `waterCache` on
 *  `Renderer`, which owns the cache and threads it through `drawWater`. */
export interface WaterCache { W: number; H: number; pts: Vec[]; coast: Vec[] }

export function buildWaterCache(world: World): WaterCache {
  const spec = world.level.water!;
  const { W, H } = world;
  const pts = spec.map(f => ({ x: f.x * W, y: f.y * H }));
  // shore highlight is the coastline only — points sitting exactly on a
  // screen corner exist purely to close the fill against the edge.
  const isCorner = (f: Vec): boolean => (f.x === 0 || f.x === 1) && (f.y === 0 || f.y === 1);
  const coast = spec.map((f, i) => (isCorner(f) ? null : pts[i])).filter((p): p is Vec => p !== null);
  return { W, H, pts, coast };
}

/** Draws the water (if any) and returns the cache to draw next frame — built
 *  fresh only when missing or the viewport size changed, exactly as the
 *  method version did with `this.waterCache`. */
export function drawWater(frame: FrameCtx, world: World, cache: WaterCache | null): WaterCache | null {
  const spec = world.level.water!;
  if (spec.length < 2) return cache;
  const { ctx } = frame;
  const { W, H } = world;
  if (!cache || cache.W !== W || cache.H !== H) cache = buildWaterCache(world);
  const { pts, coast } = cache;

  ctx.fillStyle = world.level.palette.water ?? COL.water;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.closePath();
  ctx.fill();

  if (coast.length > 1) {
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(coast[0].x, coast[0].y);
    for (let i = 1; i < coast.length - 1; i++) {
      const mx = (coast[i].x + coast[i + 1].x) / 2, my = (coast[i].y + coast[i + 1].y) / 2;
      ctx.quadraticCurveTo(coast[i].x, coast[i].y, mx, my);
    }
    const last = coast[coast.length - 1];
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  }
  return cache;
}

export function drawGrass(frame: FrameCtx, r: Runway, fieldColor: string): void {
  const { ctx } = frame;
  ctx.save();
  ctx.translate(r.x1, r.y1); ctx.rotate(r.ang);
  ctx.fillStyle = fieldColor;
  roundRect(ctx, -24, -r.w * 1.35, r.len + 48, r.w * 2.7, 24); ctx.fill();
  ctx.restore();
}

export function drawTaxiway(frame: FrameCtx, a: Runway, b: Runway, deepColor: string): void {
  const { ctx } = frame;
  const am = { x: a.x1 + (a.x2 - a.x1) * 0.6, y: a.y1 + (a.y2 - a.y1) * 0.6 };
  const bm = { x: b.x1 + (b.x2 - b.x1) * 0.6, y: b.y1 + (b.y2 - b.y1) * 0.6 };
  ctx.strokeStyle = deepColor; ctx.lineWidth = 10; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(am.x, am.y); ctx.lineTo(bm.x, bm.y); ctx.stroke();
}

export function drawRunway(frame: FrameCtx, r: Runway): void {
  const { ctx } = frame;
  ctx.save();
  ctx.translate(r.x1, r.y1); ctx.rotate(r.ang);
  // approach corridor: faint dashes on the extended centreline
  ctx.strokeStyle = 'rgba(59,59,59,0.18)';
  ctx.lineWidth = 2;
  ctx.setLineDash([4, 8]);
  ctx.beginPath(); ctx.moveTo(-42, 0); ctx.lineTo(-10, 0); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = COL.strip;
  roundRect(ctx, -6, -r.w / 2, r.len + 12, r.w, 5); ctx.fill();
  // centreline dashes
  ctx.strokeStyle = COL.line; ctx.lineWidth = 2;
  ctx.setLineDash([10, 12]);
  ctx.beginPath(); ctx.moveTo(14, 0); ctx.lineTo(r.len - 10, 0); ctx.stroke();
  ctx.setLineDash([]);
  // threshold bars at entry
  ctx.fillStyle = COL.line;
  for (let i = -2; i <= 2; i++) { if (i === 0) continue; ctx.fillRect(2, i * 5 - 1.2, 8, 2.4); }
  ctx.restore();
  // entry marker: crisp ring + dot, gently breathing
  const pu = pulse();
  ctx.strokeStyle = `rgba(59,59,59,${0.3 + 0.25 * pu})`;
  ctx.lineWidth = 1.6;
  ctx.beginPath(); ctx.arc(r.x1, r.y1, 7, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = 'rgba(248,247,242,0.95)';
  ctx.beginPath(); ctx.arc(r.x1, r.y1, 2.6, 0, Math.PI * 2); ctx.fill();
}

export function drawPad(frame: FrameCtx, pd: Pad): void {
  const { ctx } = frame;
  ctx.fillStyle = COL.strip;
  ctx.beginPath(); ctx.arc(pd.x, pd.y, pd.r, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = COL.line; ctx.lineWidth = 1.8;
  ctx.beginPath(); ctx.arc(pd.x, pd.y, pd.r - 3.5, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = COL.line;
  ctx.font = '600 11px -apple-system,sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('H', pd.x, pd.y + 0.5);
  const pu = pulse();
  ctx.strokeStyle = `rgba(59,59,59,${0.25 + 0.2 * pu})`;
  ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.arc(pd.x, pd.y, pd.r + 5, 0, Math.PI * 2); ctx.stroke();
}
